/* @flow */
import _ from 'lodash';

import type {
  Action,
  CartItem,
  LiferayError,
  MergedLicenseComponent,
  ReduxState,
} from '../../types';

// ===========================
//   STATE TYPE
// ===========================
export type CartState = {
  cartItems: CartItem[],
  isInitialized: boolean,
  isFetching: boolean,
  worklistItemsBeingAdded: number[],
  cartItemsBeingDeleted: number[],
  cartItemsBeingBought: number[],
  selectedComponentsByItem: {
    [cartItemId: $PropertyType<CartItem, 'id'>]: $PropertyType<MergedLicenseComponent, 'id'>[],
  },
  error: ?LiferayError,
};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: CartState = {
  cartItems: [],
  isInitialized: false,
  isFetching: false,
  worklistItemsBeingAdded: [],
  cartItemsBeingDeleted: [],
  cartItemsBeingBought: [],
  selectedComponentsByItem: {},
  error: null,
};

const getAllComponentsSelected = (cartItems: CartItem[]) => {
  const selectedComponentsByItem = {};
  cartItems.forEach(item => {
    selectedComponentsByItem[item.id] = (item.worklistItem.mergedComponents || []).map(
      component => component.id
    );
  });
  return selectedComponentsByItem;
};

const copySelectedComponentsForCartItems = (
  currentCartItems: CartItem[],
  currentSelectedComponentsByItem,
  targetCartItems: CartItem[]
) => {
  const selectedComponentsByItem = {};
  targetCartItems.forEach(cartItem => {
    if (!currentSelectedComponentsByItem.hasOwnProperty(cartItem.id)) {
      return;
    }
    const components = cartItem.worklistItem.mergedComponents || [];
    const selectedComponents = components.filter(({ id }) =>
      currentSelectedComponentsByItem[cartItem.id].includes(id)
    );
    if (selectedComponents.length === 0) {
      return;
    }
    selectedComponentsByItem[cartItem.id] = selectedComponents.map(({ id }) => id);
  });
  const currentCartItemIds = currentCartItems.map(item => item.id);
  const newCartItems = targetCartItems.filter(item => !currentCartItemIds.includes(item.id));
  const selectedComponentsByNewItems = getAllComponentsSelected(newCartItems);
  return _.merge(selectedComponentsByItem, selectedComponentsByNewItems);
};

// ===========================
//   REDUCER
// ===========================
export default (state: CartState = initialState, action: Action): CartState => {
  switch (action.type) {
    case 'FIND_CART_ITEMS_REQUEST': {
      return {
        ...state,
        isFetching: true,
        error: null,
      };
    }
    case 'FIND_CART_ITEMS_SUCCESS': {
      let selectedComponentsByItem = { ...state.selectedComponentsByItem };
      if (state.cartItems.length === 0) {
        selectedComponentsByItem = getAllComponentsSelected(action.payload);
      } else {
        selectedComponentsByItem = copySelectedComponentsForCartItems(
          state.cartItems,
          selectedComponentsByItem,
          action.payload
        );
      }
      return {
        ...state,
        cartItems: action.payload,
        selectedComponentsByItem,
        isInitialized: true,
        isFetching: false,
      };
    }
    case 'FIND_CART_ITEMS_FAILURE': {
      return { ...state, error: action.payload, isFetching: false };
    }
    case 'BUY_CART_ITEMS_REQUEST': {
      const cartItems = action.payload;
      return {
        ...state,
        error: null,
        cartItemsBeingBought: cartItems.map(cartItem => cartItem.id),
      };
    }
    case 'BUY_CART_ITEMS_SUCCESS': {
      const boughtItems = action.payload;
      const { cartItems } = state;
      const selectedComponentsByItem = { ...state.selectedComponentsByItem };
      const updatedCartItems = cartItems
        .map(cartItem => {
          const boughtItem = boughtItems.find(item => item.id === cartItem.id);
          if (boughtItem != null) {
            const boughtComponentsIds = (boughtItem.worklistItem.mergedComponents || []).map(
              component => component.id
            );
            const remainingComponents = (cartItem.worklistItem.mergedComponents || []).filter(
              component => !boughtComponentsIds.includes(component.id)
            );
            selectedComponentsByItem[boughtItem.id] = remainingComponents.map(c => c.id);
            if (remainingComponents.length === 0) {
              delete selectedComponentsByItem[boughtItem.id];
              return null;
            }
            return {
              ...cartItem,
              worklistItem: {
                ...cartItem.worklistItem,
                mergedComponents: remainingComponents,
              },
            };
          }
          return cartItem;
        })
        .filter(cartItem => cartItem != null);
      return {
        ...state,
        // $FlowFixMe
        cartItems: updatedCartItems,
        cartItemsBeingBought: [],
        selectedComponentsByItem,
      };
    }
    case 'BUY_CART_ITEMS_FAILURE': {
      return { ...state, error: action.payload, cartItemsBeingBought: [] };
    }
    case 'DELETE_CART_ITEMS_REQUEST': {
      const cartItems = action.payload;
      return {
        ...state,
        error: null,
        cartItemsBeingDeleted: cartItems.map(cartItem => cartItem.id),
      };
    }
    case 'DELETE_CART_ITEMS_SUCCESS': {
      const deletedItemsIds = action.payload;
      const selectedComponentsByItem = { ...state.selectedComponentsByItem };
      deletedItemsIds.forEach(id => {
        delete selectedComponentsByItem[id];
      });
      return {
        ...state,
        cartItems: state.cartItems.filter(cartItem => !deletedItemsIds.includes(cartItem.id)),
        cartItemsBeingDeleted: [],
        selectedComponentsByItem,
      };
    }
    case 'DELETE_CART_ITEMS_FAILURE': {
      return { ...state, error: action.payload, cartItemsBeingDeleted: [] };
    }
    case 'SELECT_ALL_CART_ITEMS': {
      const selectedComponentsByItem = getAllComponentsSelected(state.cartItems);
      return {
        ...state,
        selectedComponentsByItem,
      };
    }
    case 'DESELECT_ALL_CART_ITEMS': {
      return {
        ...state,
        selectedComponentsByItem: {},
      };
    }
    case 'TOGGLE_SELECT_ALL_CART_ITEMS': {
      const { cartItems, selectedComponentsByItem } = state;
      const isSelectedAll =
        cartItems.length !== 0 && Object.keys(selectedComponentsByItem).length === cartItems.length;
      if (isSelectedAll) {
        return {
          ...state,
          selectedComponentsByItem: {},
        };
      } else {
        const selectedComponentsByItem = getAllComponentsSelected(state.cartItems);
        return {
          ...state,
          selectedComponentsByItem,
        };
      }
    }
    case 'TOGGLE_SELECT_CART_ITEM': {
      const cartItemId = action.payload;
      const { cartItems, selectedComponentsByItem } = state;
      const item = cartItems.find(item => item.id === cartItemId);
      const itemComponentIds = ((item && item.worklistItem.mergedComponents) || []).map(
        component => component.id
      );
      const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
      if (selectedComponentsByItem.hasOwnProperty(cartItemId)) {
        delete nextSelectedComponentsByItem[cartItemId];
      } else {
        nextSelectedComponentsByItem[cartItemId] = itemComponentIds;
      }
      return { ...state, selectedComponentsByItem: nextSelectedComponentsByItem };
    }
    case 'TOGGLE_SELECT_CART_ITEM_COMPONENT': {
      const { cartItemId, componentId } = action.payload;
      const { selectedComponentsByItem } = state;
      const itemComponentIds = selectedComponentsByItem[cartItemId] || [];
      const selectedComponentIds = itemComponentIds.includes(componentId)
        ? itemComponentIds.filter(id => id !== componentId)
        : [...itemComponentIds, componentId];
      const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
      if (selectedComponentIds.length > 0) {
        nextSelectedComponentsByItem[cartItemId] = selectedComponentIds;
      } else {
        delete nextSelectedComponentsByItem[cartItemId];
      }
      return { ...state, selectedComponentsByItem: nextSelectedComponentsByItem };
    }
    case 'LOGOUT_SUCCESS':
      return {
        ...initialState,
        isInitialized: state.isInitialized,
      };
    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================
export const getCartItemById = (state: ReduxState, id: number): ?CartItem => {
  return state.cart.cartItems.find(x => x.id === id);
};

export const getSelectedCartItems = (state: ReduxState): CartItem[] => {
  const { cartItems, selectedComponentsByItem } = state.cart;
  const selectedCartItems = cartItems
    .filter(cartItem => selectedComponentsByItem[cartItem.id] != null)
    .map(cartItem => {
      cartItem.worklistItem.mergedComponents = (
        cartItem.worklistItem.mergedComponents || []
      ).filter(
        component =>
          selectedComponentsByItem[cartItem.id] != null &&
          selectedComponentsByItem[cartItem.id].includes(component.id)
      );
      return cartItem;
    });
  return selectedCartItems;
};

export const isAdding = (state: ReduxState): boolean => {
  return state.cart.worklistItemsBeingAdded.length > 0;
};

export const isDeleting = (state: ReduxState): boolean => {
  return state.cart.cartItemsBeingDeleted.length > 0;
};

export const isBuying = (state: ReduxState): boolean => {
  return state.cart.cartItemsBeingBought.length > 0;
};

export const getIsLoading = (state: ReduxState): boolean => {
  return state.cart.isFetching || isAdding(state) || isDeleting(state) || isBuying(state);
};

export const getNumberOfItemsInCart = (state: ReduxState): number => {
  return state.cart.cartItems.length;
};
